import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Skeleton,
  Button,
  InputAdornment,
  TextField,
  Tabs,
  Tab
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import apiClient from '../utils/apiInterceptor';
import '../styles/nexusTheme.css';
import './ProjectsPage.css';

const PROJECT_TYPE_ALL = 'all';
const PROJECT_TYPE_AUTO = 'auto';
const PROJECT_TYPE_ASSIST = 'assist';

const isAuto = (n) => (n.creationType || 'auto') !== 'assist';

const ProjectsPage = () => {
  const navigate = useNavigate();
  const [novels, setNovels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [isRetrying, setIsRetrying] = useState(false);
  const [search, setSearch] = useState('');
  const [projectTypeFilter, setProjectTypeFilter] = useState(PROJECT_TYPE_ALL);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuNovelId, setMenuNovelId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, novelId: null, title: '' });

  // 带重试机制的项目列表加载函数
  const loadNovels = useCallback(async (retryCount = 0) => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await apiClient.get('/novels/user/me');
      // 后端明确返回失败，尝试重试
      if (response.data && response.data.success === false) {
        if (retryCount < 2) {
          console.warn(`ProjectsPage: 第${retryCount + 1}次加载失败，${2000 * (retryCount + 1)}ms后重试...`);
          setIsRetrying(true);
          await new Promise(resolve => setTimeout(resolve, 2000 * (retryCount + 1)));
          setIsRetrying(false);
          return loadNovels(retryCount + 1);
        }
        throw new Error(response.data.message || '加载项目列表失败');
      }
      if (response.data && Array.isArray(response.data.data)) {
        setNovels(response.data.data);
      } else if (response.data && Array.isArray(response.data)) {
        setNovels(response.data);
      } else {
        setNovels([]);
      }
    } catch (error) {
      console.error('ProjectsPage: 加载作品失败:', error);
      if (retryCount < 2) {
        console.warn(`ProjectsPage: 第${retryCount + 1}次加载失败，${2000 * (retryCount + 1)}ms后重试...`);
        setIsRetrying(true);
        await new Promise(resolve => setTimeout(resolve, 2000 * (retryCount + 1)));
        setIsRetrying(false);
        return loadNovels(retryCount + 1);
      }
      const friendlyMsg = error.userMessage || error.message || '加载项目列表失败，请检查网络后重试';
      setLoadError(friendlyMsg);
      setNovels([]);
    } finally {
      setLoading(false);
      setIsRetrying(false);
    }
  }, []);

  useEffect(() => {
    loadNovels();
  }, []); // loadNovels 用 useCallback 包装，依赖稳定，无需放入 deps

  const bySearch = (n) =>
    !search.trim() || (n.title && n.title.toLowerCase().includes(search.toLowerCase()));
  const byType = (n) => {
    const type = n.creationType || 'auto';
    if (projectTypeFilter === PROJECT_TYPE_ALL) return true;
    if (projectTypeFilter === PROJECT_TYPE_AUTO) return type !== 'assist';
    return type === 'assist';
  };
  const filtered = novels.filter((n) => bySearch(n) && byType(n));

  // 计算总字数
  const calcWords = (n) => {
    const raw = n.totalWords || n.wordCount || 0;
    if (raw >= 10000) return `${(raw / 10000).toFixed(1)}w`;
    if (raw >= 1000) return `${(raw / 1000).toFixed(1)}k`;
    return raw || 0;
  };

  // 格式化时间
  const fmtTime = (d) => {
    if (!d) return '--';
    try {
      const date = new Date(d);
      const now = new Date();
      const diff = now - date;
      if (diff < 60000) return '刚刚';
      if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`;
      if (diff < 604800000) return `${Math.floor(diff / 86400000)}天前`;
      return date.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' });
    } catch {
      return '--';
    }
  };

  const handleMenuOpen = (e, novelId) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuNovelId(novelId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuNovelId(null);
  };

  const handleDeleteClick = () => {
    const novel = novels.find((n) => (n._id || n.id) === menuNovelId);
    setDeleteDialog({ open: true, novelId: menuNovelId, title: novel?.title || '' });
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    const id = deleteDialog.novelId;
    try {
      await apiClient.delete(`/novels/${id}`);
      setNovels((prev) => prev.filter((n) => (n._id || n.id) !== id));
    } catch (err) {
      console.error('删除失败:', err);
    } finally {
      setDeleteDialog({ open: false, novelId: null, title: '' });
    }
  };

  const totalAuto = novels.filter(isAuto).length;
  const totalAssist = novels.filter((n) => !isAuto(n)).length;

  return (
    <Box className="nexus-layout nexus-page-enter-active projects-page">
      {/* 顶部栏 */}
      <header className="nexus-header projects-header">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
          <Tooltip title="返回">
            <IconButton
              onClick={() => navigate('/tools')}
              sx={{ color: 'var(--nexus-text-secondary)', p: { xs: 0.5, sm: 1 } }}
            >
              <ArrowBackIcon sx={{ fontSize: { xs: 20, sm: 22 } }} />
            </IconButton>
          </Tooltip>
          <Box
            sx={{
              width: { xs: 38, sm: 44 },
              height: { xs: 38, sm: 44 },
              borderRadius: '12px',
              background: 'linear-gradient(135deg, var(--nexus-primary) 0%, var(--nexus-accent) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 20px var(--nexus-glow-cyan)',
              flexShrink: 0
            }}
          >
            <LibraryBooksIcon sx={{ fontSize: { xs: 20, sm: 24 }, color: '#0a0b12' }} />
          </Box>
          <Box sx={{ overflow: 'hidden' }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                color: 'var(--nexus-text-primary)',
                letterSpacing: '0.02em',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              我的创作项目
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'var(--nexus-text-muted)', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
            >
              {totalAuto} 个全自动 · {totalAssist} 个辅助写作
            </Typography>
          </Box>
        </Box>
        <TextField
          size="small"
          placeholder="搜索项目..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            width: { xs: 140, sm: 200 },
            display: { xs: 'none', sm: 'flex' },
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              bgcolor: 'var(--nexus-bg-elevated)',
              border: '1px solid var(--nexus-border)',
              color: 'var(--nexus-text-primary)',
              fontSize: '0.85rem',
              '&:hover': { borderColor: 'var(--nexus-border-hover)' },
              '& fieldset': { border: 'none' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'var(--nexus-text-muted)', fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
      </header>

      {/* 移动端搜索框 */}
      <Box sx={{ px: 2, pb: 1, display: { xs: 'block', sm: 'none' } }}>
        <TextField
          size="small"
          placeholder="搜索项目..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              bgcolor: 'var(--nexus-bg-elevated)',
              border: '1px solid var(--nexus-border)',
              color: 'var(--nexus-text-primary)',
              fontSize: '0.85rem',
              '&:hover': { borderColor: 'var(--nexus-border-hover)' },
              '& fieldset': { border: 'none' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'var(--nexus-text-muted)', fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <main className="projects-main">
        {/* Tab栏 */}
        <Box className="projects-tabs-wrap">
          <Tabs
            value={projectTypeFilter}
            onChange={(_, v) => setProjectTypeFilter(v)}
            sx={{
              minHeight: { xs: 36, sm: 40 },
              '& .MuiTab-root': {
                color: 'var(--nexus-text-muted)',
                textTransform: 'none',
                fontSize: { xs: '0.8rem', sm: '0.85rem' },
                fontWeight: 600,
                minHeight: { xs: 36, sm: 40 },
                px: { xs: 1.5, sm: 2.5 },
                py: 0,
                '&.Mui-selected': { color: 'var(--nexus-primary)' }
              },
              '& .MuiTabs-indicator': {
                background: 'var(--nexus-primary)',
                height: 2,
                borderRadius: '2px 2px 0 0'
              },
              '& .MuiTabs-flexContainer': { gap: { xs: 0, sm: 0.5 } }
            }}
          >
            <Tab value={PROJECT_TYPE_ALL} label={`全部 ${filtered.length}`} />
            <Tab value={PROJECT_TYPE_AUTO} label={`全自动 ${totalAuto}`} />
            <Tab value={PROJECT_TYPE_ASSIST} label={`辅助 ${totalAssist}`} />
          </Tabs>

          {/* 快速操作按钮 */}
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, alignItems: 'center', flexShrink: 0 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AutoStoriesIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate('/auto-novel')}
              className="projects-quick-btn-auto"
              sx={{
                textTransform: 'none',
                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                py: { xs: '4px', sm: '6px' },
                px: { xs: 1.5, sm: 2 },
                borderColor: 'var(--nexus-border-hover)',
                color: 'var(--nexus-text-secondary)',
                borderRadius: '8px',
                '&:hover': { borderColor: 'var(--nexus-accent)', color: 'var(--nexus-accent)', bgcolor: 'var(--nexus-accent-subtle)' },
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>全自动创作</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>全自动</Box>
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate('/tools')}
              className="nexus-btn-primary"
              sx={{
                textTransform: 'none',
                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                py: { xs: '4px', sm: '6px' },
                px: { xs: 1.5, sm: 2 },
                borderRadius: '8px',
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>新建辅助写作</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>新建</Box>
            </Button>
          </Box>
        </Box>

        {/* 项目列表 */}
        {loading || isRetrying ? (
          <Box className="projects-list">
            {[1, 2, 3, 4, 5].map((i) => (
              <Box key={i} className="project-item-skeleton">
                <Box className="skeleton-left">
                  <Skeleton variant="rounded" width={40} height={40} sx={{ bgcolor: 'var(--nexus-bg-elevated)', borderRadius: '10px' }} />
                </Box>
                <Box className="skeleton-right">
                  <Skeleton variant="text" width="60%" height={22} sx={{ bgcolor: 'var(--nexus-bg-elevated)' }} />
                  <Skeleton variant="text" width="40%" height={16} sx={{ bgcolor: 'var(--nexus-bg-elevated)', mt: 0.5 }} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : loadError ? (
          /* 加载失败状态 */
          <Box className="projects-empty">
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '20px',
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <LibraryBooksIcon sx={{ fontSize: 36, color: '#f87171', opacity: 0.6 }} />
            </Box>
            <Typography sx={{ color: '#f87171', fontWeight: 600, mb: 1 }}>
              {loadError}
            </Typography>
            <Typography sx={{ color: 'var(--nexus-text-muted)', fontSize: '0.85rem', mb: 3 }}>
              请检查网络连接后重试
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => loadNovels()}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                borderColor: 'rgba(239,68,68,0.3)',
                color: '#f87171',
                '&:hover': { borderColor: '#f87171', bgcolor: 'rgba(239,68,68,0.05)' }
              }}
            >
              重新加载
            </Button>
          </Box>
        ) : filtered.length === 0 ? (
          <Box className="projects-empty">
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '20px',
                background: 'var(--nexus-bg-elevated)',
                border: '1px solid var(--nexus-border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
              }}
            >
              <LibraryBooksIcon sx={{ fontSize: 36, color: 'var(--nexus-text-muted)' }} />
            </Box>
            <Typography sx={{ color: 'var(--nexus-text-secondary)', fontWeight: 600, mb: 0.5, fontSize: '0.95rem' }}>
              {search.trim() ? '没有找到匹配的项目' : '暂无创作项目'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--nexus-text-muted)', fontSize: '0.8rem', mb: 2.5 }}>
              {search.trim() ? '换个关键词试试' : '开始全自动创作或新建辅助写作项目'}
            </Typography>
            {!search.trim() && (
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AutoStoriesIcon />}
                  onClick={() => navigate('/auto-novel')}
                  sx={{
                    textTransform: 'none',
                    borderColor: 'var(--nexus-border-hover)',
                    color: 'var(--nexus-text-secondary)',
                    fontSize: '0.8rem',
                    borderRadius: '8px',
                    '&:hover': { borderColor: 'var(--nexus-accent)', color: 'var(--nexus-accent)' }
                  }}
                >
                  全自动创作
                </Button>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/tools')}
                  className="nexus-btn-primary"
                  sx={{ textTransform: 'none', fontSize: '0.8rem', borderRadius: '8px' }}
                >
                  新建辅助写作
                </Button>
              </Box>
            )}
          </Box>
        ) : (
          <Box className="projects-list">
            {filtered.map((novel, index) => {
              const id = novel._id || novel.id;
              const auto = isAuto(novel);
              // 🔥 后端列表已瘦身：chapters 不再随响应传输，优先用 chapterCount 数字（兼容旧缓存）
              const chapterCount = novel.chapterCount ?? novel.chapters?.length ?? 0;
              // 已生成章节数（有正文），后端 generatedCount 统计；辅助创作类型无此概念
              const generatedCount = auto
                ? (novel.generatedCount ?? novel.chapters?.filter(ch => ch?.content)?.length ?? 0)
                : null;
              const words = calcWords(novel);
              const updated = fmtTime(novel.updatedAt || novel.updated);

              return (
                <Box
                  key={id}
                  className="project-item"
                  onClick={() => navigate(`/workspace/${id}`)}
                  sx={{
                    animation: `assistFadeIn 0.35s ease ${index * 0.04}s both`,
                    cursor: 'pointer',
                    '&:hover': { transform: 'translateX(4px)' },
                    '&:hover .project-item-arrow': { opacity: 1, transform: 'translateX(0)' },
                    '&:hover .project-item-delete-btn': { opacity: 1 }
                  }}
                >
                  {/* 左侧图标 */}
                  <Box className="project-item-icon">
                    <Box
                      sx={{
                        width: { xs: 38, sm: 44 },
                        height: { xs: 38, sm: 44 },
                        borderRadius: { xs: '10px', sm: '12px' },
                        background: auto
                          ? 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(6, 182, 212, 0.08) 100%)'
                          : 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(59, 130, 246, 0.08) 100%)',
                        border: auto
                          ? '1px solid rgba(6, 182, 212, 0.25)'
                          : '1px solid rgba(59, 130, 246, 0.25)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: auto
                          ? '0 0 12px rgba(6, 182, 212, 0.15)'
                          : '0 0 12px rgba(59, 130, 246, 0.15)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {auto ? (
                        <AutoAwesomeIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: 'var(--nexus-accent)' }} />
                      ) : (
                        <EditNoteIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: 'var(--nexus-primary)' }} />
                      )}
                    </Box>
                  </Box>

                  {/* 中间内容 */}
                  <Box className="project-item-content">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography
                        className="project-item-title"
                        sx={{
                          fontSize: { xs: '0.88rem', sm: '0.95rem' },
                          fontWeight: 700,
                          color: 'var(--nexus-text-primary)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                          maxWidth: { xs: 140, sm: 240 }
                        }}
                      >
                        {novel.title || '未命名项目'}
                      </Typography>
                      {/* 类型徽章 */}
                      <Box
                        sx={{
                          px: 0.75,
                          py: 0.25,
                          borderRadius: '4px',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          letterSpacing: '0.02em',
                          flexShrink: 0,
                          background: auto ? 'rgba(6, 182, 212, 0.12)' : 'rgba(59, 130, 246, 0.12)',
                          color: auto ? 'var(--nexus-accent)' : 'var(--nexus-primary)',
                          border: `1px solid ${auto ? 'rgba(6, 182, 212, 0.2)' : 'rgba(59, 130, 246, 0.2)'}`,
                          display: { xs: (auto ? 'flex' : 'flex'), sm: 'flex' }
                        }}
                      >
                        {auto ? '全自动' : '辅助'}
                      </Box>
                    </Box>

                    {/* 描述/备注 */}
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        color: 'var(--nexus-text-muted)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: { xs: 160, sm: 260 }
                      }}
                    >
                      {novel.description || '暂无备注'}
                    </Typography>

                    {/* 底部统计 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mt: 0.75 }}>
                      {auto && generatedCount !== null ? (
                        <>
                          {/* 已生成 / 总章节（全自动类型） */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box
                              component="span"
                              sx={{
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                fontFamily: 'monospace',
                                color: chapterCount > 0 && generatedCount >= chapterCount
                                  ? '#34d399'
                                  : 'var(--nexus-accent)',
                                transition: 'color 0.3s ease'
                              }}
                            >
                              {generatedCount}
                              <Box component="span" sx={{ color: 'var(--nexus-text-muted)', fontWeight: 600 }}>
                                /{chapterCount}
                              </Box>
                            </Box>
                            <Box component="span" sx={{ fontSize: '0.65rem', color: 'var(--nexus-text-muted)' }}>
                              {chapterCount > 0 && generatedCount >= chapterCount ? '章·已生成完' : '章已生成'}
                            </Box>
                          </Box>
                          {/* 生成进度条 */}
                          <Box
                            sx={{
                              width: { xs: 36, sm: 48 },
                              height: 3,
                              borderRadius: 2,
                              overflow: 'hidden',
                              bgcolor: 'rgba(148, 163, 184, 0.15)',
                              flexShrink: 0
                            }}
                          >
                            <Box
                              sx={{
                                height: '100%',
                                width: `${chapterCount > 0 ? Math.min(100, (generatedCount / chapterCount) * 100) : 0}%`,
                                borderRadius: 2,
                                background: chapterCount > 0 && generatedCount >= chapterCount
                                  ? 'linear-gradient(90deg, #10b981 0%, #34d399 100%)'
                                  : 'linear-gradient(90deg, var(--nexus-accent) 0%, rgba(56, 189, 248, 0.5) 100%)',
                                transition: 'width 0.6s ease'
                              }}
                            />
                          </Box>
                        </>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box
                            component="span"
                            sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--nexus-text-secondary)', fontFamily: 'monospace' }}
                          >
                            {chapterCount}
                          </Box>
                          <Box component="span" sx={{ fontSize: '0.65rem', color: 'var(--nexus-text-muted)' }}>
                            章
                          </Box>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box
                          component="span"
                          sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--nexus-text-secondary)', fontFamily: 'monospace' }}
                        >
                          {words}
                        </Box>
                        <Box component="span" sx={{ fontSize: '0.65rem', color: 'var(--nexus-text-muted)' }}>
                          字
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box
                          component="span"
                          sx={{ fontSize: '0.65rem', color: 'var(--nexus-text-muted)' }}
                        >
                          {updated}
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* 右侧操作区 */}
                  <Box className="project-item-actions">
                    <IconButton
                      size="small"
                      className="project-item-delete-btn"
                      onClick={(e) => { e.stopPropagation(); handleDeleteClick(); }}
                      sx={{
                        opacity: 0,
                        color: 'var(--nexus-text-muted)',
                        p: 0.5,
                        transition: 'opacity 0.2s ease',
                        '&:hover': { color: 'var(--nexus-error)', bgcolor: 'rgba(239, 68, 68, 0.1)' }
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <Box
                      className="project-item-arrow"
                      sx={{
                        opacity: 0.6,
                        transform: 'translateX(-4px)',
                        transition: 'all 0.25s ease',
                        color: 'var(--nexus-text-muted)'
                      }}
                    >
                      <ArrowForwardIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </main>

      {/* 删除确认弹窗 */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, novelId: null, title: '' })}
        PaperProps={{
          sx: {
            backgroundColor: 'var(--nexus-bg-card)',
            backdropFilter: 'blur(24px)',
            border: '1px solid var(--nexus-border)',
            borderRadius: '16px',
            boxShadow: '0 0 60px rgba(0,0,0,0.6)',
            minWidth: { xs: 280, sm: 340 }
          }
        }}
      >
        <DialogTitle sx={{ color: 'var(--nexus-text-primary)', fontWeight: 700, fontSize: '1rem', pb: 1 }}>
          确认删除项目
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography sx={{ color: 'var(--nexus-text-secondary)', fontSize: '0.9rem', lineHeight: 1.6 }}>
            确定删除「<Box component="span" sx={{ color: 'var(--nexus-text-primary)', fontWeight: 600 }}>{deleteDialog.title}</Box>」吗？此操作无法撤销。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, novelId: null, title: '' })}
            sx={{
              textTransform: 'none',
              color: 'var(--nexus-text-muted)',
              borderColor: 'var(--nexus-border)',
              border: '1px solid var(--nexus-border)',
              borderRadius: '8px',
              '&:hover': { borderColor: 'var(--nexus-border-hover)' }
            }}
          >
            取消
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              textTransform: 'none',
              backgroundColor: 'var(--nexus-error)',
              borderRadius: '8px',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#dc2626' }
            }}
          >
            确认删除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectsPage;
